import React, { useRef } from "react";
import gsap from "gsap";
import { Sun, Moon } from "lucide-react";
import { useTheme } from "@/studio/lib/theme";

/**
 * Premium light/dark switch with a cinematic GSAP clip-path wipe.
 *
 * Two variants:
 *  • default — a labelled pill (Light / Dark) used on desktop
 *  • compact — a 40px round icon button used inside the mobile header so the
 *    bar never overflows on small screens (320px and up)
 *
 * Both share the exact same wipe transition.
 */
const NEXT_BG = { dark: "#030303", light: "#F7F7F5" };

export default function ThemeToggle({ compact = false }) {
  const { theme, setTheme } = useTheme();
  const dark = theme === "dark";
  const btnRef = useRef(null);
  const knobRef = useRef(null);
  const animatingRef = useRef(false);

  const handleClick = () => {
    if (animatingRef.current) return;
    animatingRef.current = true;
    const next = dark ? "light" : "dark";
    const btn = btnRef.current;
    const rect = btn.getBoundingClientRect();
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;
    const maxR = Math.hypot(
      Math.max(cx, window.innerWidth - cx),
      Math.max(cy, window.innerHeight - cy),
    );

    const overlay = document.createElement("div");
    overlay.setAttribute("aria-hidden", "true");
    overlay.style.cssText = `
      position:fixed;inset:0;z-index:9998;pointer-events:none;
      background:${NEXT_BG[next]};
      clip-path:circle(0px at ${cx}px ${cy}px);
      will-change:clip-path,opacity;
    `;
    document.body.appendChild(overlay);

    const tl = gsap.timeline({
      onComplete: () => {
        animatingRef.current = false;
        overlay.remove();
      },
    });

    // pill knob slide (only present in the default variant)
    const knob = knobRef.current;
    if (knob) {
      const knobLeft = next === "dark" ? "calc(50% + 0px)" : "4px";
      tl.to(knob, { left: knobLeft, duration: 0.55, ease: "expo.inOut" }, 0);
    }

    tl.to(
      overlay,
      {
        clipPath: `circle(${maxR}px at ${cx}px ${cy}px)`,
        duration: 0.85,
        ease: "power3.inOut",
      },
      0,
    );
    tl.add(() => setTheme(next), 0.6);
    tl.to(overlay, { opacity: 0, duration: 0.35, ease: "power2.out" }, 0.78);
  };

  if (compact) {
    return (
      <button
        ref={btnRef}
        type="button"
        onClick={handleClick}
        aria-label="Toggle light or dark theme"
        data-testid="theme-toggle"
        className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-[var(--line)] bg-[var(--paper-2)] text-[var(--ink)]"
      >
        {dark ? <Moon className="h-[18px] w-[18px]" /> : <Sun className="h-[18px] w-[18px]" />}
      </button>
    );
  }

  return (
    <button
      ref={btnRef}
      type="button"
      onClick={handleClick}
      role="switch"
      aria-checked={dark}
      aria-label="Toggle light or dark theme"
      data-cursor="big"
      data-testid="theme-toggle"
      className="relative inline-flex h-9 w-[116px] items-center rounded-full border border-[var(--line)] bg-[var(--paper-2)] p-1"
    >
      <span
        ref={knobRef}
        className="absolute top-1 bottom-1 rounded-full bg-[var(--ink)]"
        style={{
          width: "calc(50% - 4px)",
          left: dark ? "calc(50% + 0px)" : "4px",
        }}
      />
      <span
        className="relative z-10 flex-1 text-center text-[11px] font-semibold tracking-wide transition-colors duration-500"
        style={{ color: dark ? "var(--ink-faint)" : "var(--paper)" }}
      >
        Light
      </span>
      <span
        className="relative z-10 flex-1 text-center text-[11px] font-semibold tracking-wide transition-colors duration-500"
        style={{ color: dark ? "var(--paper)" : "var(--ink-faint)" }}
      >
        Dark
      </span>
    </button>
  );
}
